import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useModal } from '../../components/useModal'
import TextInput from '../../components/FormElements/TextInput'
import { MechanicalCounter } from 'mechanical-counter'
import { formatNumber } from '../../lib/utils/formatNumber'
import { useGetAudienceById } from '../../api/dealership/customers/audiences/useGetAudienceById'
import { useEditAudience } from '../../api/dealership/customers/audiences/useEditAudience'
import toast from 'react-hot-toast'
import CustomQueryBuilder from '../../components/AudienceBuilder/QueryBuilder'
import NumberFlow from '@number-flow/react'
import { useQueryEstimator } from '../../api/dealership/customers/audiences/useQueryEstimator'
import {
  defaultFields,
  filterAudienceQueryBuilderRulesForSubmission,
  leadsFields,
  leadVehicleAttributes,
  mapDbFiltersToQueryBuilder,
  mossyCustomFields,
  parseQueryBuilderRules,
  salesFields,
  saleVehicleAttributes,
  serviceFields,
} from '../../lib/utils/audienceHelpers'
import { LoadingComponent, Button, RangeDisplay } from '@demandlocal/components'
import { RuleWithSubOptions } from '../../lib/types/Audience'
import { useDealership } from '../../store/zustand/dealershipStore'
import { getRuleFromConfig } from '../../components/AudienceBuilder/utils'

const EditAudience = ({ audience_id }: { audience_id: string }): JSX.Element => {
  const { data: audience, isLoading: isLoadingAudience } = useGetAudienceById(audience_id)
  const formContext = useForm({
    mode: 'onSubmit',
  })
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isSubmitting },
  } = formContext

  const { closeModal } = useModal()

  const [filteredAudience, setFilteredAudience] = useState({ count: 0 })

  const { mutateAsync: editAudience } = useEditAudience()

  const { mutate: postEstimateUsers } = useQueryEstimator()

  const dealership = useDealership()

  const isMossyDealership = useMemo(
    () => dealership?.dealership?.toLowerCase().includes('mossy'),
    [dealership?.dealership],
  )

  const audienceType = audience?.audience_type

  useEffect(() => {
    if (!isLoadingAudience) {
      reset({
        audience_name: audience?.audience_name,
        description: audience?.description,
        audience_type: audience?.audience_type,
        groups: audience?.filters?.map((group) => {
          return {
            ...group,
            rules: group?.rules?.map((rule) => {
              return {
                ...rule,
                field: getRuleFromConfig(rule?.field?.value),
              }
            }),
          }
        }),
      })
    }
  }, [
    audience?.audience_name,
    audience?.description,
    audience?.audience_type,
    audience?.filters,
    isLoadingAudience,
    reset,
  ])

  const onQueryChange = useCallback(() => {
    const newRules = getValues('groups') ?? []
    postEstimateUsers(
      {
        queryData: newRules,
      },
      {
        onSuccess: (data) => {
          setFilteredAudience(data?.data)
        },
      },
    )
  }, [getValues, postEstimateUsers])

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const toastId = toast.loading('Updating audience...')
    await editAudience({
      data: {
        _id: audience_id,
        audience_name: values.audience_name,
        description: values.description,
        filters: values.groups,
      },
    })
    toast.success('Audience updated successfully', { id: toastId })
    closeModal()
  }

  return (
    <FormProvider {...formContext}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full'}>
        {isLoadingAudience && <LoadingComponent title={'Loading audience'} />}
        <div className={'flex flex-1 flex-col overflow-auto'}>
          <span className={'font-bold text-[28px] text-primary-xiketic'}>
            Edit {audience?.audience_name}
          </span>
          <div className={'pt-8 pb-8'}>
            <div>
              <TextInput
                variant={'material'}
                rules={{ required: 'This field is required' }}
                inverted
                name={'audience_name'}
                label={'Audience name'}
                control={control}
              />
            </div>
          </div>
          <div>
            <div>
              <TextInput
                rules={{ required: 'This field is required' }}
                inverted
                name={'description'}
                label={'Description'}
                control={control}
              />
            </div>
          </div>
          <div className={'pt-10 flex flex-row justify-between items-center'}>
            <span className={'font-bold text-base text-primary-xiketic'}>
              Who are you targeting
            </span>
            <div className={'flex flex-col items-start cursor-pointer gap-2'}>
              <div>
                <span className={'font-medium text-base text-primary-xiketic'}>
                  Audience size
                </span>
              </div>
              <div className="bg-secondary-sunglow/20 p-3 font-bold">
                <NumberFlow
                  value={filteredAudience.count}
                  className={'part-[suffix]:text-primary-persian-blue'}
                />
              </div>
            </div>
          </div>
          <div className={'mt-8'}>
            <CustomQueryBuilder onChange={onQueryChange} />
          </div>
        </div>
        <div className={'h-20 items-center flex justify-between border-t-2 py-4 mt-4'}>
          <Button
            variant={'secondary'}
            onClick={(e) => {
              e.preventDefault()
              closeModal()
            }}>
            Close
          </Button>
          <Button disabled={filteredAudience.count === 0 || isSubmitting} type={'submit'}>
            Update audience
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default EditAudience
